/* eslint-disable max-lines */
import { v4 } from 'uuid';
import isPlainObject from 'lodash/isPlainObject';
import { getRecoil, setRecoil } from 'recoil-nexus';

import { iaRa } from 'lib/helpers/utility';

import { clinicalNote as clinicalNoteState, intelligenceAiState } from 'components/state';
import {
  activePromptConversationsState,
  intelligenceChatIdState,
  intelligenceChatMessageIdState,
  isAIGeneratingState,
  myScribeAILoadingState,
  myScribeAutoMapActivePromptIdState,
  myScribeAutoMapSelectedNoteTypeState,
  promptListState,
  selectedChatTemplateState
} from 'components/practice/charts/ClinicalNote/MyScribeAI/state';

import { assembleChunks, clearChunkBuffer } from './chunkAssembly';
import { isFollowUpFromConversations, stripNoteFlags, updatePromptList } from './helpers';

let isGenerationStarted = false;
const chunkBuffers = {};

export const resetAutoMapGenerationState = () => {
  isGenerationStarted = false;
  Object.keys(chunkBuffers).forEach((chatId) => clearChunkBuffer(chunkBuffers, chatId));
};

const endGeneration = () => {
  setRecoil(myScribeAILoadingState, false);
  setRecoil(isAIGeneratingState, false);
  isGenerationStarted = false;
};

const updateAutoMapTemplate = (data) => {
  const {
    vertical,
    response,
    done = false,
    abort = false,
    isRegenerate = false,
    isDescription = false,
    isNote = true,
    note,
    ...rest
  } = data;

  if (rest?.destId == null || Number(rest.destId) !== Number(getRecoil(clinicalNoteState)?.id)) {
    return;
  }

  const activePromptConversations = getRecoil(activePromptConversationsState);
  const isFollowUp = isFollowUpFromConversations(activePromptConversations);

  try {
    if (isDescription) {
      setRecoil(promptListState, (prevPrompts = []) =>
        updatePromptList(
          prevPrompts,
          rest?.intelligence_chat_id,
          rest?.intelligenceChat,
          isDescription
        )
      );

      if (abort) endGeneration();
      return;
    }

    const intelligenceChatId = getRecoil(intelligenceChatIdState);

    // Always clear loading/generating on done|abort, even if event id mismatches.
    // Without this, a late or mismatched terminal event leaves the UI stuck with
    // disabled Preview/Regenerate and the stop button still showing.
    if (done || abort) {
      setRecoil(
        myScribeAutoMapActivePromptIdState,
        abort ? rest?.intelligenceChat?.id : rest?.intelligence_chat_id
      );
      endGeneration();
    }

    if (
      rest?.intelligence_chat_id &&
      intelligenceChatId &&
      rest?.intelligence_chat_id !== intelligenceChatId
    ) {
      return;
    }

    // Handle initial generation (first request, not regenerate, not follow-up)
    if (!isGenerationStarted && !isRegenerate && !isFollowUp && !done && !abort) {
      const selectedTemplate = getRecoil(selectedChatTemplateState);
      setRecoil(promptListState, (prevPrompts = []) =>
        updatePromptList(prevPrompts, rest?.intelligence_chat_id, {
          ...rest?.intelligenceChat,
          template_id: rest?.intelligenceChat?.template_id ?? selectedTemplate?.id ?? null
        })
      );
      setRecoil(myScribeAutoMapActivePromptIdState, rest?.intelligence_chat_id);
      setRecoil(isAIGeneratingState, true);
      setRecoil(myScribeAILoadingState, true);
      isGenerationStarted = true;
      return;
    }

    // Handle regenerate case
    if (isRegenerate && !done && !abort && !isGenerationStarted) {
      setRecoil(isAIGeneratingState, true);
      setRecoil(myScribeAILoadingState, true);
      isGenerationStarted = true;
    }
    if (!isNote) {
      setRecoil(myScribeAILoadingState, false);
    }

    if (rest?.intelligence_chat_id) {
      setRecoil(intelligenceChatIdState, rest?.intelligence_chat_id);
    }
    const newAiClinicalNoteState = getRecoil(intelligenceAiState);
    if (newAiClinicalNoteState?.timeoutTimeout)
      clearTimeout(newAiClinicalNoteState?.timeoutTimeout);

    setPromptStates({
      done,
      abort,
      response,
      path: vertical,
      isRegenerate,
      isNote,
      isFollowUp,
      note: isNote ? note : null,
      ...rest
    });
  } catch (error) {
    console.error('❌ Error', error);
  }
};

const setPromptStates = (intelligenceAi) => {
  if (intelligenceAi?.response) {
    if (!intelligenceAi?.isRegenerate) {
      setRecoil(myScribeAutoMapActivePromptIdState, intelligenceAi?.intelligence_chat_id);
    }
    if (intelligenceAi?.done || intelligenceAi?.abort) {
      setRecoil(myScribeAutoMapSelectedNoteTypeState, intelligenceAi.noteType);
      if (intelligenceAi?.intelligenceChat) {
        const { id, name, description, noteType, template_id, created_at } =
          intelligenceAi?.intelligenceChat || {};
        setRecoil(promptListState, (prevPrompts) => {
          if (prevPrompts?.[0]?.id === id && !intelligenceAi?.isFollowUp) {
            const newPrompts = [...prevPrompts];
            newPrompts[0] = {
              ...prevPrompts[0],
              id,
              name,
              description,
              noteType,
              template_id: template_id ?? prevPrompts[0]?.template_id ?? null,
              created_at: created_at ?? prevPrompts[0]?.created_at
            };
            return newPrompts;
          }
          return prevPrompts;
        });
      }
      if (intelligenceAi?.intelligenceChatMessage) {
        // Safety net: final message persisted means generation is over.
        setRecoil(myScribeAILoadingState, false);
        setRecoil(isAIGeneratingState, false);
        setRecoil(intelligenceChatMessageIdState, intelligenceAi?.intelligenceChatMessage?.id);
        setRecoil(activePromptConversationsState, (prevState) => {
          let newPrompt = [...iaRa(prevState)];
          if (newPrompt[newPrompt?.length - 1]?.agent === 'ai') {
            newPrompt[newPrompt?.length - 1] = {
              ...intelligenceAi?.intelligenceChatMessage,
              emCPTs: intelligenceAi?.emCPTs,
              note: intelligenceAi?.note
            };
          } else {
            newPrompt = [
              ...iaRa(prevState),
              {
                ...intelligenceAi?.intelligenceChatMessage,
                emCPTsText: intelligenceAi?.emCPTsText,
                note: intelligenceAi?.note
              }
            ];
          }
          return newPrompt;
        });
      }
    }
    if (!intelligenceAi?.isNote || intelligenceAi?.done) {
      const chatId = intelligenceAi?.intelligence_chat_id;
      const orderId = intelligenceAi?.response.orderId;
      const value = intelligenceAi?.response.value;
      const isResponseObject = isPlainObject(intelligenceAi?.response);
      const isLast = intelligenceAi?.done && !isResponseObject;

      const fullMessage = assembleChunks(chunkBuffers, chatId, value, orderId);

      const rawMessage =
        intelligenceAi?.done && !isResponseObject ? intelligenceAi?.response : fullMessage;
      const message = stripNoteFlags(rawMessage);
      const newMessageObj = {
        id: intelligenceAi?.intelligenceChatMessage?.id || v4(),
        message,
        isNote: intelligenceAi?.isNote,
        note: intelligenceAi?.note,
        emCPTsText: intelligenceAi?.emCPTsText,
        agent: 'ai'
      };

      setRecoil(activePromptConversationsState, (prevState = []) => {
        let newConversation = [...iaRa(prevState)];
        if (newConversation[newConversation?.length - 1]?.agent === 'ai') {
          newConversation[newConversation?.length - 1] = newMessageObj;
        } else {
          newConversation = [...iaRa(prevState), newMessageObj];
        }
        return newConversation;
      });

      if (isLast) {
        clearChunkBuffer(chunkBuffers, chatId);
        setRecoil(isAIGeneratingState, false);
      }
    }
  }
};

export default updateAutoMapTemplate;
